/** Booking systems on the Connect screen (plan §5.2). Each one the easy flow supports has a
 *  front-end module (its words, and the ops words for any credential field the backend words
 *  for the client); the fields themselves come from the backend (`credential_fields` on the
 *  status). The rest render as "Advanced setup" choices that open the advanced create flow
 *  with the booking system prefilled. */
import type { EasyRestech, EasyRestechStatus, EasyStatus } from '@/api/easyOnboarding';
import type { WebhookSetupMode } from '@/api/integrations';

import type { Restech } from '../../wizard/types';
import { BOKUN } from './bokun';
import { FAREHARBOR } from './fareharbor';

/** How a booking system is named in sentences, and how it usually sends us bookings: all the
 *  screens after Connect need. */
export interface RestechWords {
  /** The vendor's name in sentences ("Checking with FareHarbor…"). */
  vendor: string;
  /** The vendor's word for a product, plural ("products"). */
  products: string;
  /** How this vendor usually sends us bookings: the Ready step's words until the tenant's
   *  booking integration answers its own `webhook_setup_mode`. */
  webhookSetup?: WebhookSetupMode;
}

export interface EasyRestechModule extends RestechWords {
  code: EasyRestech;
  /** Ops words for a credential field, by its `name`, where the backend's label or hint
   *  (written for the client) does not suit the console. Unnamed fields use the backend's. */
  fieldWords?: Readonly<Record<string, { label?: string; hint?: string }>>;
  webhookSetup: WebhookSetupMode;
}

export const EASY_RESTECHS: Record<EasyRestech, EasyRestechModule> = {
  fareharbor: FAREHARBOR,
  bokun: BOKUN,
};

export interface BookingSystemChoice {
  code: Restech;
  label: string;
  easy: EasyRestechModule | null;
}

/** The choice cards, in the advanced wizard's order (its `RESTECH_OPTIONS`, minus None). */
export const BOOKING_SYSTEMS: BookingSystemChoice[] = [
  { code: 'fareharbor', label: 'FareHarbor', easy: FAREHARBOR },
  { code: 'ventrata', label: 'Ventrata', easy: null },
  { code: 'bokun', label: 'Bókun', easy: BOKUN },
  { code: 'rezdy', label: 'Rezdy', easy: null },
  { code: 'trekksoft', label: 'TrekkSoft', easy: null },
  { code: 'xml', label: 'XML', easy: null },
  { code: 'other', label: 'Other', easy: null },
];

export function easyModule(restech: string | null | undefined): EasyRestechModule | null {
  return restech && restech in EASY_RESTECHS ? EASY_RESTECHS[restech as EasyRestech] : null;
}

/** The words for a tenant's booking system: its module's, else its choice card's label (a
 *  restech this console has no easy module for reads by its own name, never as another one). */
export function restechWords(restech: string | null | undefined): RestechWords {
  const module = easyModule(restech);
  if (module) return module;
  const label = BOOKING_SYSTEMS.find((b) => b.code === restech)?.label;
  return { vendor: label ?? (restech || 'the booking system'), products: 'products' };
}

/** The easy restechs this environment lists, in choice-card order: each with its status row. */
export function easyChoices(
  status: EasyStatus,
): { module: EasyRestechModule; row: EasyRestechStatus }[] {
  return BOOKING_SYSTEMS.flatMap((b) => {
    const row = b.easy && status.restechs.find((r) => r.code === b.easy!.code);
    return b.easy && row ? [{ module: b.easy, row }] : [];
  });
}

/** The booking system Connect opens on: the chosen one when listed, else the first available
 *  easy one in choice-card order, else the first listed (its row says why it is unavailable). */
export function initialRestech(
  status: EasyStatus,
  chosen: EasyRestech | null,
): EasyRestechModule | null {
  const choices = easyChoices(status);
  return (
    choices.find((c) => c.module.code === chosen)?.module ??
    choices.find((c) => c.row.available)?.module ??
    choices[0]?.module ??
    null
  );
}
